import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, X, ImageOff, Flag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useHubAvatarUrl } from "@/lib/hub/useHubAvatarUrl";

/**
 * ADMIN — LR Hub avatar moderation queue.
 * Reads/writes only `hub_settings` through admin-gated RPCs.
 */

interface PendingRow {
  user_id: string;
  display_name: string;
  handle: string;
  avatar_path: string | null;
  updated_at: string;
  email: string | null;
  full_name: string | null;
}

function PendingCard({
  row,
  onDecide,
  busy,
}: {
  row: PendingRow;
  onDecide: (userId: string, status: "approved" | "rejected") => void;
  busy: boolean;
}) {
  const { url, loading } = useHubAvatarUrl(row.avatar_path);
  return (
    <div className="flex items-center gap-4 rounded-lg border border-[#1e2530] bg-[#0d1117] p-3">
      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#1e2530]">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-[#6b7280]" />
        ) : url ? (
          <img src={url} alt={row.display_name} className="h-16 w-16 object-cover" />
        ) : (
          <ImageOff className="h-5 w-5 text-[#6b7280]" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">
          {row.display_name} <span className="font-normal text-[#9ca3af]">@{row.handle}</span>
        </p>
        <p className="truncate text-xs text-[#9ca3af]">
          {row.full_name || "—"} · {row.email || "—"}
        </p>
        <p className="mt-0.5 text-[11px] text-[#6b7280]">
          Submitted {new Date(row.updated_at).toLocaleString()}
        </p>
      </div>
      <div className="flex flex-shrink-0 gap-2">
        <Button
          size="sm"
          className="h-8 bg-[#14b8a6] text-[#04211f] hover:bg-[#14b8a6]/90"
          disabled={busy}
          onClick={() => onDecide(row.user_id, "approved")}
        >
          <Check className="mr-1.5 h-3.5 w-3.5" />
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-[#2a3240] text-[#e5e7eb] hover:bg-[#1e2530]"
          disabled={busy}
          onClick={() => onDecide(row.user_id, "rejected")}
        >
          <X className="mr-1.5 h-3.5 w-3.5" />
          Deny
        </Button>
      </div>
    </div>
  );
}

export default function AdminHubModeration() {
  const qc = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-hub-pending-avatars"],
    queryFn: async (): Promise<PendingRow[]> => {
      const { data, error } = await supabase.rpc("hub_admin_list_pending_avatars");
      if (error) throw error;
      return (data ?? []) as PendingRow[];
    },
  });

  const decide = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: "approved" | "rejected" }) => {
      const { error } = await supabase.rpc("hub_admin_set_avatar_status", {
        _user_id: userId,
        _status: status,
      });
      if (error) throw error;
      return status;
    },
    onSuccess: (status) => {
      toast.success(status === "approved" ? "Photo approved" : "Photo denied");
      qc.invalidateQueries({ queryKey: ["admin-hub-pending-avatars"] });
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Action failed.");
    },
    onSettled: () => setPendingId(null),
  });

  const rows = useMemo(() => data ?? [], [data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-white">LR Hub Moderation</h1>
        <p className="text-xs text-[#9ca3af]">
          Approve or deny member-uploaded LR Hub profile photos. Members show generated initials
          until a photo is approved.
        </p>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#6b7280]" />
        </div>
      ) : error ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-6 text-center text-xs text-[#f87171]">
          Could not load the moderation queue.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-8 text-center text-xs text-[#9ca3af]">
          No photos waiting for review.
        </p>
      ) : (
        <>
          <Badge variant="outline" className="border-amber-500/40 text-[10px] text-amber-400">
            {rows.length} pending
          </Badge>
          <div className="space-y-2">
            {rows.map((r) => (
              <PendingCard
                key={r.user_id}
                row={r}
                busy={decide.isPending && pendingId === r.user_id}
                onDecide={(userId, status) => {
                  setPendingId(userId);
                  decide.mutate({ userId, status });
                }}
              />
            ))}
          </div>
        </>
      )}

      <MemberReportsQueue />
      <FlaggedPostsQueue />
      <SocialModerationQueue />
    </div>
  );
}

/* ── Member reports (LR Hub) ──────────────────────────────────────────
   Reports filed from the feed's 3-dot menu land in `user_reports` with
   report_type 'hub_post' / 'hub_comment'. Dismiss keeps the content;
   Remove hides it. Admin gating lives inside the RPCs. ────────────── */

interface HubReportRow {
  id: string;
  report_type: string;
  target_identifier: string;
  issue_type: string;
  details: string | null;
  status: string;
  created_at: string;
  reporter_id: string;
  reporter_name: string | null;
  reporter_handle: string | null;
  content_body: string | null;
  content_author_name: string | null;
  content_author_handle: string | null;
  content_deleted: boolean | null;
}

function MemberReportsQueue() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-hub-reports"],
    queryFn: async (): Promise<HubReportRow[]> => {
      const { data, error } = await supabase.rpc("hub_admin_list_reports");
      if (error) throw error;
      return (data ?? []) as HubReportRow[];
    },
  });

  const resolve = useMutation({
    mutationFn: async ({ reportId, action }: { reportId: string; action: "dismiss" | "remove" }) => {
      const { error } = await supabase.rpc("hub_admin_resolve_report", {
        _report_id: reportId,
        _action: action,
      });
      if (error) throw error;
      return action;
    },
    onSuccess: (action) => {
      toast.success(action === "remove" ? "Content removed" : "Report dismissed");
      qc.invalidateQueries({ queryKey: ["admin-hub-reports"] });
      qc.invalidateQueries({ queryKey: ["admin-hub-flagged-posts"] });
      qc.invalidateQueries({ queryKey: ["admin-hub-flagged-comments"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Action failed."),
    onSettled: () => setBusyId(null),
  });

  const rows = data ?? [];
  const pending = rows.filter((r) => r.status === "pending");

  return (
    <div className="space-y-3 border-t border-[#1e2530] pt-6">
      <div className="flex items-center gap-2">
        <Flag className="h-4 w-4 text-amber-400" />
        <h2 className="text-sm font-semibold text-white">Member reports</h2>
        {pending.length > 0 && (
          <Badge variant="outline" className="border-amber-500/40 text-[10px] text-amber-400">
            {pending.length} pending
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-[#6b7280]" />
        </div>
      ) : error ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-6 text-center text-xs text-[#f87171]">
          Could not load member reports.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-6 text-center text-xs text-[#9ca3af]">
          No member reports.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-white">
                    {r.report_type === "hub_post" ? "Post" : "Comment"} by{" "}
                    {r.content_author_name || "Unknown"}{" "}
                    {r.content_author_handle && (
                      <span className="font-normal text-[#9ca3af]">@{r.content_author_handle}</span>
                    )}
                  </p>
                  <p className="truncate text-[11px] text-[#6b7280]">
                    Reported by {r.reporter_name || "member"}
                    {r.reporter_handle ? ` @${r.reporter_handle}` : ""} ·{" "}
                    {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {r.status !== "pending" ? (
                    <Badge variant="outline" className="border-[#2a3240] text-[10px] text-[#9ca3af]">
                      Resolved
                    </Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-[#2a3240] text-[#e5e7eb] hover:bg-[#1e2530]"
                        disabled={resolve.isPending && busyId === r.id}
                        onClick={() => {
                          setBusyId(r.id);
                          resolve.mutate({ reportId: r.id, action: "dismiss" });
                        }}
                      >
                        <Check className="mr-1.5 h-3.5 w-3.5" />
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10"
                        disabled={
                          (resolve.isPending && busyId === r.id) || Boolean(r.content_deleted)
                        }
                        onClick={() => {
                          setBusyId(r.id);
                          resolve.mutate({ reportId: r.id, action: "remove" });
                        }}
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-[#11161d] p-2.5 text-xs text-[#e5e7eb]">
                {r.content_deleted && !r.content_body
                  ? "This content is no longer available."
                  : r.content_body}
              </p>

              <p className="mt-2 text-[11px] text-amber-400">
                Reason: {r.issue_type}
                {r.content_deleted ? " · content already removed" : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Flagged LR Hub comments ──────────────────────────────────────────
   Comments/replies flagged by `moderate-hub-comment` (or queued by a model
   outage). Approve publishes them and bumps the post's comment count;
   Reject hides them. Admin gating lives inside the RPCs. ────────────── */

interface FlaggedCommentRow {
  id: string;
  post_id: string;
  parent_comment_id: string | null;
  author_id: string;
  display_name: string | null;
  handle: string | null;
  full_name: string | null;
  email: string | null;
  body: string;

  moderation_status: string;
  moderation_reason: string | null;
  moderation_categories: string[] | null;
  created_at: string;
}

/* ── Social Moderation (LR Hub comments & replies) ────────────────────
   Every comment/reply from a non-admin member is stored as `pending` and
   is invisible to everyone but its author until approved here. LaneRunner
   admins (admin_roles) post straight through. Rejecting keeps the record
   for history — it is never deleted. ─────────────────────────────────── */

type ModStatusFilter = "pending" | "approved" | "rejected" | "all";

interface ModerationCommentRow extends FlaggedCommentRow {
  post_id: string;
  post_body: string | null;
  parent_body: string | null;
  company_name: string | null;
  moderation_status: string;
}

const STATUS_TABS: { key: ModStatusFilter; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function SocialModerationQueue() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<ModStatusFilter>("pending");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-hub-moderation-comments", status],
    queryFn: async (): Promise<ModerationCommentRow[]> => {
      const { data, error } = await (supabase.rpc as never as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>)(
        "hub_admin_list_moderation_comments",
        { _status: status },
      );
      if (error) throw new Error(error.message);
      return (data ?? []) as ModerationCommentRow[];
    },
  });

  const decide = useMutation({
    mutationFn: async ({ commentId, status }: { commentId: string; status: "approved" | "rejected" }) => {
      const { error } = await supabase.rpc("hub_admin_set_comment_status", {
        _comment_id: commentId,
        _status: status,
      });
      if (error) throw error;
      return status;
    },
    onSuccess: (status) => {
      toast.success(status === "approved" ? "Comment approved — now live" : "Comment rejected");
      qc.invalidateQueries({ queryKey: ["admin-hub-moderation-comments"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Action failed."),
    onSettled: () => setBusyId(null),
  });

  const rows = data ?? [];

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-white">Social Moderation</h2>
        <p className="text-xs text-[#9ca3af]">
          Every member comment and reply waits here until you approve it. Approving publishes it under
          the post; rejecting keeps the record but never shows it publicly.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            className={
              status === t.key
                ? "rounded-md bg-[#14b8a6]/15 px-3 py-1.5 text-[11.5px] font-bold text-[#14b8a6]"
                : "rounded-md px-3 py-1.5 text-[11.5px] font-semibold text-[#9ca3af] hover:bg-[#1e2530] hover:text-white"
            }
          >
            {t.label}
            {status === t.key && !isLoading ? ` (${rows.length})` : ""}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#6b7280]" />
        </div>
      ) : error ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-6 text-center text-xs text-[#f87171]">
          Could not load comments.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-8 text-center text-xs text-[#9ca3af]">
          {status === "pending" ? "Nothing waiting for review." : "No comments in this list."}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-white">
                    {r.display_name || r.full_name || "LR Member"}{" "}
                    {r.handle ? <span className="font-normal text-[#9ca3af]">@{r.handle}</span> : null}
                  </p>
                  <p className="truncate text-[11px] text-[#6b7280]">
                    {[r.full_name, r.email, r.company_name].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="truncate text-[11px] text-[#6b7280]">
                    {new Date(r.created_at).toLocaleString()} ·{" "}
                    {r.parent_comment_id ? "reply" : "comment"} ·{" "}
                    <span
                      className={
                        r.moderation_status === "approved"
                          ? "text-[#14b8a6]"
                          : r.moderation_status === "rejected"
                            ? "text-[#f87171]"
                            : "text-amber-400"
                      }
                    >
                      {r.moderation_status}
                    </span>
                  </p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <Button
                    size="sm"
                    className="h-8 bg-[#14b8a6] text-[#04211f] hover:bg-[#14b8a6]/90"
                    disabled={(decide.isPending && busyId === r.id) || r.moderation_status === "approved"}
                    onClick={() => {
                      setBusyId(r.id);
                      decide.mutate({ commentId: r.id, status: "approved" });
                    }}
                  >
                    <Check className="mr-1.5 h-3.5 w-3.5" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-[#2a3240] text-[#e5e7eb] hover:bg-[#1e2530]"
                    disabled={(decide.isPending && busyId === r.id) || r.moderation_status === "rejected"}
                    onClick={() => {
                      setBusyId(r.id);
                      decide.mutate({ commentId: r.id, status: "rejected" });
                    }}
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" />
                    Reject
                  </Button>
                </div>
              </div>

              {r.parent_body ? (
                <p className="mt-2 truncate rounded-md bg-[#11161d] px-2.5 py-1.5 text-[11px] text-[#9ca3af]">
                  Replying to: {r.parent_body}
                </p>
              ) : null}

              <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-[#11161d] p-2.5 text-xs text-[#e5e7eb]">
                {r.body}
              </p>

              {r.post_body ? (
                <p className="mt-2 truncate text-[11px] text-[#6b7280]">On post: {r.post_body}</p>
              ) : null}

              {r.moderation_reason || r.moderation_categories?.length ? (
                <p className="mt-2 text-[11px] text-amber-400">
                  {r.moderation_reason || "Held for review"}
                  {r.moderation_categories?.length ? ` · ${r.moderation_categories.join(", ")}` : ""}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Flagged LR Hub posts ─────────────────────────────────────────────
   Posts flagged by the `moderate-hub-post` AI moderation (or queued by a
   model outage). Approve puts them live; Reject hides them. Admin gating
   lives inside the RPCs (is_lanerunner_admin). ─────────────────────── */

interface FlaggedPostRow {
  id: string;
  author_id: string;
  body: string;
  content_type: string;
  tags: string[] | null;
  moderation_reason: string | null;
  moderation_categories: string[] | null;
  created_at: string;
  display_name: string | null;
  handle: string | null;
  email: string | null;
  full_name: string | null;
  moderation_status?: string | null;
}

function FlaggedPostsQueue() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-hub-flagged-posts"],
    queryFn: async (): Promise<FlaggedPostRow[]> => {
      const { data, error } = await supabase.rpc("hub_admin_list_flagged_posts");
      if (error) throw error;
      return (data ?? []) as FlaggedPostRow[];
    },
  });

  const decide = useMutation({
    mutationFn: async ({ postId, status }: { postId: string; status: "approved" | "rejected" }) => {
      const { error } = await supabase.rpc("hub_admin_set_post_status", {
        _post_id: postId,
        _status: status,
      });
      if (error) throw error;
      return status;
    },
    onSuccess: (status) => {
      toast.success(status === "approved" ? "Post approved — now live" : "Post rejected");
      qc.invalidateQueries({ queryKey: ["admin-hub-flagged-posts"] });
      qc.invalidateQueries({ queryKey: ["hub-feed"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Action failed."),
    onSettled: () => setBusyId(null),
  });

  const rows = data ?? [];

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-white">Posts Awaiting Approval</h2>
        <p className="text-xs text-[#9ca3af]">
          Every member post waits here until you approve it (LaneRunner team posts go live instantly).
          Approving publishes it to the LR Hub feed; rejecting keeps the record but never shows it.
        </p>
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#6b7280]" />
        </div>
      ) : error ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-6 text-center text-xs text-[#f87171]">
          Could not load flagged posts.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-8 text-center text-xs text-[#9ca3af]">
          No posts waiting for review.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">
                    {r.display_name || r.full_name || "LR Member"}{" "}
                    {r.handle ? <span className="font-normal text-[#9ca3af]">@{r.handle}</span> : null}
                  </p>
                  <p className="truncate text-[11px] text-[#6b7280]">
                    {r.email || "—"} · {new Date(r.created_at).toLocaleString()} · {r.content_type}
                    {r.moderation_status ? ` · ${r.moderation_status}` : ""}
                  </p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <Button
                    size="sm"
                    className="h-8 bg-[#14b8a6] text-[#04211f] hover:bg-[#14b8a6]/90"
                    disabled={decide.isPending && busyId === r.id}
                    onClick={() => {
                      setBusyId(r.id);
                      decide.mutate({ postId: r.id, status: "approved" });
                    }}
                  >
                    <Check className="mr-1.5 h-3.5 w-3.5" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-[#2a3240] text-[#e5e7eb] hover:bg-[#1e2530]"
                    disabled={decide.isPending && busyId === r.id}
                    onClick={() => {
                      setBusyId(r.id);
                      decide.mutate({ postId: r.id, status: "rejected" });
                    }}
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" />
                    Reject
                  </Button>
                </div>
              </div>

              <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-[#11161d] p-2.5 text-xs text-[#e5e7eb]">
                {r.body}
              </p>

              <p className="mt-2 text-[11px] text-amber-400">
                {r.moderation_reason || "Flagged for review"}
                {r.moderation_categories?.length ? ` · ${r.moderation_categories.join(", ")}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

